'use client';
import { useState } from 'react';

import Image from 'next/image';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import Typography from '@components/Typography';
import { DurationIcon, PhoneIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import NavigationCard from '@components/views/landing/common/NavigationCard/NavigationCard';
import { C4CSalesItemType } from '@api/v3';

import {
  InfoContainer,
  LandingCardWrapper,
  MainProps,
  Name,
  InfoLink,
  NameLocationContainer,
  Wrapper,
  DurationContainer,
  DurationText,
  EverythingIncluded,
  Location,
  StyledButton,
  CustomIconWrapper,
  Separator,
} from './LandingCardStyles';
const LetUsCallYou = dynamic(
  () => import('@components/ui/LetUsCallYou/LetUsCallYou')
);

export enum LandingCardType {
  BIG_CARD = 'BIG_CARD',
  SMALL_CARD = 'SMALL_CARD',
  MOBILE_CARD = 'MOBILE_CARD',
}

export interface ILandingCardProps {
  src: string;
  alt?: string;
  info: IInfoProps;
  cardType: LandingCardType;
  itemType?: string;
  price?: number;
  showLetUsCallYou?: boolean;
}

export interface IInfoProps {
  discount?: number;
  name?: string;
  price: number;
  subText?: string; //can be price subtext or image top text
  location?: string;
  link?: string;
  boardType?: string;
  productId?: number;
  duration?: string;
}

type Props = {
  info: IInfoProps;
  itemType?: string;
  cardType: LandingCardType;
  onNavigate?: () => void;
  showLetUsCallYou?: boolean;
};

const Info = ({ info, itemType, onNavigate, showLetUsCallYou }: Props) => {
  const [letUsCallYou, setLetUsCallYou] = useState<boolean>(false);

  const toggleLetUsCallYou = () => {
    setLetUsCallYou(!letUsCallYou);
  };

  return (
    <InfoContainer>
      {info.duration ? (
        <DurationContainer>
          <DurationIcon width={14} />
          <DurationText>{info.duration}</DurationText>
        </DurationContainer>
      ) : null}
      <MainProps>
        {info.boardType ? (
          <EverythingIncluded>{info?.boardType}</EverythingIncluded>
        ) : null}
        {itemType === 'Tour' ? null : <Location>{info?.location}</Location>}
      </MainProps>
      <NameLocationContainer>
        <Name title={info?.name ?? ''}>
          <InfoLink
            href={info.link as string}
            title={info.name}
            onClick={onNavigate}
            prefetch={false}
          >
            <Typography variant='caption' color='D400' weight='SemiBold'>
              {info?.name}
            </Typography>
          </InfoLink>
        </Name>
      </NameLocationContainer>
      <Separator>
        {!showLetUsCallYou ? (
          <NavigationCard
            info={info}
            onNavigate={onNavigate}
            styles={{
              containerDisplayFlex: true,
              desktopFontSize: 18,
              mobileFontSize: 14,
            }}
          />
        ) : info.price ? (
          <NavigationCard
            info={info}
            onNavigate={onNavigate}
            styles={{
              containerDisplayFlex: true,
              desktopFontSize: 18,
              mobileFontSize: 14,
            }}
          />
        ) : (
          <StyledButton onClick={() => toggleLetUsCallYou()}>
            <CustomIconWrapper>
              <CustomIcon
                paletteColor='bluishPurple'
                icon={PhoneIcon}
                fontSize={16}
              />
            </CustomIconWrapper>
            <span>Biz Sizi Arayalım</span>
          </StyledButton>
        )}
      </Separator>

      <LetUsCallYou
        visibleLetUsCallYouModal={letUsCallYou}
        token=''
        onClose={toggleLetUsCallYou}
        entityId={String(info?.productId ?? '')}
        entityName={info?.name ?? ''}
        price={info?.price || 0}
        salesItemType={C4CSalesItemType.TUR_KULTUR}
        formCheckInDate={null}
        formCheckOutDate={null}
        hotelRoomList={[]}
      />
    </InfoContainer>
  );
};

type LandingCardProps = ILandingCardProps & { onNavigate?: () => void };

const LandingCard = ({
  src,
  info,
  cardType,
  alt,
  onNavigate = () => false,
  itemType,
  showLetUsCallYou,
}: LandingCardProps) => {
  const getClass = () => {
    switch (cardType) {
      case LandingCardType.MOBILE_CARD:
        return 'mobile-card-container';
      case LandingCardType.BIG_CARD:
        return 'big-card-container';
      default:
        return 'small-card-container';
    }
  };

  return (
    <LandingCardWrapper>
      <Wrapper
        isDurationIncluded={info.duration !== undefined}
        className={getClass()}
      >
        <Link href={info.link as string} onClick={onNavigate} prefetch={false}>
          <div className='image-wrapper'>
            <Image
              fill
              style={{ objectFit: 'cover' }}
              src={src}
              alt={alt || 'setur'}
            />
          </div>
        </Link>
        <Info
          itemType={itemType}
          info={info}
          cardType={cardType}
          onNavigate={onNavigate}
          showLetUsCallYou={showLetUsCallYou}
        />
      </Wrapper>
    </LandingCardWrapper>
  );
};

export default LandingCard;
